import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import { collection, query, onSnapshot, getDocs, writeBatch, deleteDoc, doc, orderBy, where } from 'firebase/firestore';
import { Plus, LayoutGrid, List, Tag, Search } from 'lucide-react'; 
import CustomerTable from '../components/crm/CustomerTable';
import KanbanBoard from '../components/crm/KanbanBoard';
import ConfirmationModal from '../components/common/ConfirmationModal';
import {
    buildCustomerDisplayName,
    customerMatchesQuery,
    compareCustomersByDisplayName,
} from '../utils/customerDisplay';
import { can, canTechManageCustomers, PERMISSIONS } from '../utils/permissions';

const CustomerManagementView = ({ user, currentUserProfile, companySettings, openCustomerModal, onCustomerSelect }) => {
    // Add Customer mirrors the customers CREATE rule: managers/office/consultant
    // via MANAGE_CUSTOMERS, plus a flag-on Field Technician.
    const canCreateCustomer = can(currentUserProfile, PERMISSIONS.MANAGE_CUSTOMERS) || canTechManageCustomers(currentUserProfile, companySettings);
    // LTV is a money figure — hidden from users without VIEW_FINANCIALS (techs).
    const canViewFinancials = can(currentUserProfile, PERMISSIONS.VIEW_FINANCIALS);
    const [customers, setCustomers] = useState([]);
    const [invoices, setInvoices] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    // ROUND 3c VERIFIER: 1761744000-round3c-viewmode-persist
    // Persist view preference across sessions. First-time users land on 'table'
    // (less overwhelming for orgs with many customers — kanban becomes hard to
    // scan once any column has 50+ cards). Returning users get whatever they
    // last picked. Pattern matches the column-visibility persistence in
    // CustomerTable.js.
    const VIEW_MODE_STORAGE_KEY = 'spatchy_customer_view_mode';
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
            return saved === 'board' || saved === 'table' ? saved : 'table';
        } catch {
            return 'table';
        }
    });
    useEffect(() => {
        try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode); } catch {}
    }, [viewMode]);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [allTags, setAllTags] = useState([]);
    const [filterTag, setFilterTag] = useState('all');
    const [searchTerm, setSearchTerm] = useState(''); 

    useEffect(() => {
        if (!user || !currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        // Fetch Customers
        const customerCollectionPath = `organizations/${orgId}/customers`;
        const q = query(collection(db, customerCollectionPath));
        const unsubCustomers = onSnapshot(q, (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });
        
        // FETCH ALL INVOICES FOR THE ORGANIZATION
        const unsubInvoices = onSnapshot(query(collection(db, `organizations/${orgId}/invoices`)), (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Fetch Tags
        const tagsQuery = query(collection(db, `organizations/${orgId}/tags`), orderBy('label'));
        const unsubTags = onSnapshot(tagsQuery, (snapshot) => {
            setAllTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { 
            unsubCustomers();
            unsubInvoices(); 
            unsubTags();
        };
    }, [user, currentUserProfile]);

    const filteredAndSortedCustomers = useMemo(() => {
        // Calculate LTV and attach a built display name to each customer.
        // displayName flows through the rest of this pipeline (search, sort,
        // table render) so we never depend on a top-level `name` field that
        // mobile-created customers don't have.
        const customersWithLTV = customers.map(customer => {
            const customerInvoices = invoices.filter(inv => 
                inv.customerId === customer.id && 
                inv.status && 
                inv.status.toLowerCase() !== 'draft'
            );
            
            const ltv = customerInvoices.reduce((total, inv) => total + (inv.total || 0), 0);
            return {
                ...customer,
                lifetimeValue: ltv,
                displayName: buildCustomerDisplayName(customer),
            };
        });

        let filteredCustomers = [...customersWithLTV];

        // Token-AND search across firstName, lastName, company, address, email,
        // and phone — same behavior as the mobile customer picker so the two
        // surfaces feel identical.
        if (searchTerm.trim() !== '') {
            filteredCustomers = filteredCustomers.filter(customer =>
                customerMatchesQuery(customer, searchTerm)
            );
        }

        // Filter by tag
        if (filterTag !== 'all') {
            filteredCustomers = filteredCustomers.filter(customer => 
                customer.tags?.includes(filterTag)
            );
        }

        // Sort. The 'name' key uses the display-name comparator (handles
        // missing names by sorting them to the bottom). Other keys use the
        // original generic comparison.
        if (sortConfig.key === 'name') {
            filteredCustomers.sort((a, b) =>
                compareCustomersByDisplayName(a, b, sortConfig.direction)
            );
        } else if (sortConfig.key) {
            filteredCustomers.sort((a, b) => {
                let valA = a[sortConfig.key] || 0;
                let valB = b[sortConfig.key] || 0;

                // Handle date objects from Firestore Timestamps
                if (sortConfig.key === 'createdAt') {
                    valA = a.createdAt?.toDate() || new Date(0);
                    valB = b.createdAt?.toDate() || new Date(0);
                }

                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return filteredCustomers;
    }, [customers, invoices, sortConfig, filterTag, searchTerm]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleDeleteRequest = (customerId) => {
        setCustomerToDelete(customerId);
    };
    
    const handleConfirmDelete = async () => {
        if (!customerToDelete || !currentUserProfile?.organizationId) return;
        
        const orgId = currentUserProfile.organizationId;
        const customerDocRef = doc(db, `organizations/${orgId}/customers`, customerToDelete);

        try {
            const batch = writeBatch(db);

            // Delete associated invoices
            const invoicesQuery = query(collection(db, `organizations/${orgId}/invoices`), where('customerId', '==', customerToDelete));
            const invoicesSnapshot = await getDocs(invoicesQuery);
            invoicesSnapshot.forEach(doc => batch.delete(doc.ref));

            // Delete the customer
            batch.delete(customerDocRef);

            await batch.commit();
        } catch (error) {
            console.error("Error removing customer and associated data: ", error);
        } finally {
            setCustomerToDelete(null);
        }
    };

    return (
        <>
            <div className="p-4 sm:p-6 md:p-10 w-full min-w-0">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 w-full">
                    <h1 className="text-3xl font-bold text-gray-800">Customers</h1>
                    <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                        
                        {/* --- SEARCH BAR UI --- */}
                        <div className="flex items-center border rounded-md bg-white flex-1 min-w-[200px]">
                            <Search size={20} className="text-gray-400 mx-2 flex-shrink-0" />
                            <input
                                type="text"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-2 pr-4 py-2 bg-transparent text-sm font-medium focus:outline-none focus:ring-0 border-0 w-full min-w-0"
                            />
                        </div>

                        {/* --- TAGS UI --- */}
                        <div className="flex items-center border rounded-md bg-white flex-1 sm:flex-none min-w-[150px]">
                            <Tag size={20} className="text-gray-400 mx-2 flex-shrink-0" />
                            <select 
                                value={filterTag}
                                onChange={(e) => setFilterTag(e.target.value)}
                                className="pl-2 pr-8 py-2 bg-transparent text-sm font-medium focus:outline-none focus:ring-0 border-0 w-full min-w-0"
                            >
                                <option value="all">All Tags</option>
                                {allTags.map(tag => (
                                    <option key={tag.id} value={tag.id}>{tag.label}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* --- TOGGLES & BUTTON --- */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button onClick={() => setViewMode('board')} className={`p-2 rounded-md transition-colors ${viewMode === 'board' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}><LayoutGrid size={20} /></button>
                            <button onClick={() => setViewMode('table')} className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}><List size={20} /></button>
                            
                            {canCreateCustomer && (
                                <button className="flex-1 sm:flex-none flex items-center justify-center p-2 sm:p-3 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap" onClick={() => openCustomerModal()}>
                                    <Plus size={20} className="mr-1 sm:mr-2" /> Add Customer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {isLoading ? <p className="text-center mt-8 text-gray-500">Loading customers...</p> : viewMode === 'table' ? 
                    <CustomerTable
                        customers={filteredAndSortedCustomers}
                        openModal={openCustomerModal}
                        onDeleteRequest={handleDeleteRequest}
                        requestSort={requestSort}
                        sortConfig={sortConfig}
                        onCustomerSelect={onCustomerSelect}
                        canViewFinancials={canViewFinancials}
                    /> :
                    <KanbanBoard 
                        customers={filteredAndSortedCustomers} 
                        user={user} 
                        currentUserProfile={currentUserProfile} 
                        openModal={openCustomerModal}
                        onCustomerSelect={onCustomerSelect}
                    />
                }
            </div>
            <ConfirmationModal
                isOpen={!!customerToDelete}
                onClose={() => setCustomerToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Customer"
                message="Are you sure you want to permanently delete this customer and all associated data? This action cannot be undone."
                confirmText="Yes, Delete"
            />
        </>
    );
};

export default CustomerManagementView;